<script setup lang="ts">
import { inject, onMounted, onUnmounted, ref, provide } from "vue";
import ContentSearch from "@/components/content/ContentSearch.vue";

import { useBonusReportFilters } from "@/composables/filters/bonus-report-filters";

import { searchContextSymbol } from "@/constants";
import { bonusReportKey } from "@/types/products/product-definitions";
import type { ProductFilter } from "@/composables/filters/product-filter";
import type { CategoryFilter } from "@/composables/filters/category-filter";
import type { PagingFilter } from "@/composables/filters/paging-filter";
import { categoryFilterSymbol, pagingFilterSymbol, productFilterSymbol } from "@/plugins/ContentFiltersPlugin";
import { useBonusReportsStore } from "@/stores/BonusReports";
import { storeToRefs } from "pinia";
import { useApplicationStore } from "@/stores/Application";

const {
  filteredBonusReportsPage,
  filteredBonusReports,
  hasAnyBonusReportsResults,
  searchResultsLabel,
  highlightedBonusReports
} = useBonusReportFilters();

const { bonusReportBySlug } = useBonusReportsStore();
const { setSelectedBonusReport } = useApplicationStore();
const { bonusReportCategories } = storeToRefs( useBonusReportsStore() );

provide( searchContextSymbol, {
  contentTitle: "Bonus Reports",
  contentType: bonusReportKey,
  searchPlaceholder: "Search Bonus Reports",
  contentBySlug: bonusReportBySlug,
  contentCategories: bonusReportCategories,
  filteredContentPage: filteredBonusReportsPage,
  filteredContent: filteredBonusReports,
  highlightedContent: highlightedBonusReports,
  hasAnyResults: hasAnyBonusReportsResults,
  searchResultsLabel: searchResultsLabel,
  setSelectedContent: setSelectedBonusReport,
  hideDateFilter: true
} );

const productFilter = inject< ProductFilter >( productFilterSymbol ) as ProductFilter;
const categoryFilter = inject< CategoryFilter >( categoryFilterSymbol ) as CategoryFilter;
const pagingFilter = inject< PagingFilter >( pagingFilterSymbol ) as PagingFilter;

const appStore = useApplicationStore();

const show = ref< boolean >( false );
onMounted( async () => {
  await appStore.initializeAsync();

  // Clear all product filters set
  productFilter?.resetSelectedProducts();

  // Clear all category filters set
  categoryFilter?.resetSelectedCategories();

  // Reset the page # of the results back to 0
  pagingFilter?.resetPageNumber();

  show.value = true;
} );

onUnmounted( () => show.value = false );

( async () => await appStore.initializeAsync() )();
</script>

<template>
  <transition mode="out-in" name="slide-down">
    <ContentSearch v-if="show"/>
  </transition>
</template>
